/*
  Aide au DOM — volontairement minuscule.

  Ce n'est pas un framework et ça ne doit pas le devenir : pas de réactivité,
  pas de cycle de vie, pas de diff. Juste de quoi éviter cinq lignes de
  `createElement` par élément. Une personne en service civique doit pouvoir
  lire ce fichier en entier en une minute.
*/

type Attributs = {
  class?: string
  text?: string
  html?: string
  hidden?: boolean
  [autre: string]: string | number | boolean | undefined
}

export function el<K extends keyof HTMLElementTagNameMap>(
  balise: K,
  attributs: Attributs = {},
  enfants: Array<Node | string | null | undefined> = [],
): HTMLElementTagNameMap[K] {
  const noeud = document.createElement(balise)

  for (const [cle, valeur] of Object.entries(attributs)) {
    if (valeur === undefined || valeur === false) continue

    if (cle === 'class') noeud.className = String(valeur)
    else if (cle === 'text') noeud.textContent = String(valeur)
    else if (cle === 'html') noeud.innerHTML = String(valeur)
    else if (valeur === true) noeud.setAttribute(cle, '')
    else noeud.setAttribute(cle, String(valeur))
  }

  for (const enfant of enfants) {
    if (enfant === null || enfant === undefined) continue
    noeud.append(enfant)
  }

  return noeud
}

export function vider(noeud: HTMLElement): void {
  noeud.replaceChildren()
}

export function requis<T extends HTMLElement = HTMLElement>(id: string): T {
  const noeud = document.getElementById(id)
  if (!noeud) throw new Error(`Élément #${id} absent de index.html`)
  return noeud as T
}

/** Retour haptique court. Silencieux si l'appareil ne sait pas vibrer. */
export function vibrer(ms = 15): void {
  try {
    navigator.vibrate?.(ms)
  } catch {
    /* iOS et certains Android n'exposent pas l'API : ce n'est pas une erreur */
  }
}

/** `HH:MM:SS` à partir d'un nombre de secondes. Chiffres tabulaires en CSS. */
export function chrono(secondes: number): string {
  const s = Math.max(0, Math.floor(secondes))
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(Math.floor(s / 3600))}:${p(Math.floor(s / 60) % 60)}:${p(s % 60)}`
}

/** `HH:MM` local, pour la barre d'état et le bandeau de file. */
export function heure(date: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(date.getHours())}:${p(date.getMinutes())}`
}

/** Ancienneté en minutes, arrondie, à partir d'un horodatage ISO. */
export function ancienneteMinutes(iso: string, maintenant = Date.now()): number {
  return Math.max(0, Math.round((maintenant - new Date(iso).getTime()) / 60_000))
}
